import { MAX_UPLOAD_BYTES } from '../../shared/files';
import { useEffect, useState } from 'preact/hooks';
import {
  matchInvoice,
  type InvoicePageBody,
  type MatchCheck,
  type MatchResult,
} from '../../shared/invoices/invoice';
import { fillInvoiceAmounts } from '../../shared/invoices/invoice';
import { toMinor } from '../../shared/po/po';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend, apiUpload } from '../lib/api';
import { formatDate } from '../lib/dates';

// Record a supplier invoice (P4.3; 01-SPEC §3 step 9). Procurement enters the invoice as printed
// and the quantities it bills; the match against the PO and the delivery notes is previewed here
// with the shared rule and worked out again by the broker, which stores it (D59).

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: InvoicePageBody };

interface Form {
  invoiceNo: string;
  invoiceDate: string;
  subtotal: string;
  vat: string;
  total: string;
}

const EMPTY: Form = { invoiceNo: '', invoiceDate: '', subtotal: '', vat: '', total: '' };
const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );
const qtyFmt = (v: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(v);
const num = (s: string) => (s.trim() === '' ? 0 : Number(s));
const MAX_FILE = MAX_UPLOAD_BYTES;

function CheckLine({ c, currency }: { c: MatchCheck; currency: string }) {
  const { t } = useI18n();
  const v: Record<string, string> = { ...c.values };
  for (const k of ['sum', 'expected', 'over', 'allowed', 'poTotal']) {
    if (v[k] !== undefined) v[k] = `${currency} ${money(Number(v[k]))}`;
  }
  return (
    <li class={`inv-check inv-check--${c.ok ? 'ok' : 'fail'}`}>
      <span aria-hidden="true">{c.ok ? '✓' : '✕'}</span>
      <span dir="auto">{t(`inv.check.${c.id}.${c.ok ? 'ok' : 'fail'}` as MessageKey, v)}</span>
    </li>
  );
}

function MatchPill({ m }: { m: MatchResult }) {
  const { t } = useI18n();
  return (
    <span class={`pill ${m.matched ? 'pill--success' : 'pill--danger'}`}>
      {m.matched ? t('inv.matched') : t('inv.mismatch')}
    </span>
  );
}

export function RecordInvoicePage({ prNo }: { prNo: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<Form>(EMPTY);
  const [qty, setQty] = useState<Record<number, string>>({});
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string; ref?: string | null } | null>(null);

  useEffect(() => {
    let live = true;
    apiGet<InvoicePageBody>(`/api/prs/${encodeURIComponent(prNo)}/invoices`)
      .then((data) => {
        if (!live) return;
        setLoad({ kind: 'ok', data });
        // Prefill what was received (or, for services, nothing) and not yet invoiced.
        setQty(
          Object.fromEntries(
            data.lines.map((l) => [
              l.lineNo,
              l.type === 'monthly'
                ? '0'
                : String(Math.max(0, Math.round((l.received - l.invoicedBefore) * 1000) / 1000)),
            ]),
          ),
        );
      })
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [prNo, reload]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>
          {load.code === 'forbidden'
            ? t('review.error.forbidden')
            : load.code === 'invalid_transition'
              ? t('dl.error.noPo')
              : t('req.loadError')}
        </p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }

  const data = load.data;
  const cur = data.po.currency;
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
  const lines = data.lines.map((l) => ({ lineNo: l.lineNo, qty: num(qty[l.lineNo] ?? '0') }));
  const numbersOk = [form.subtotal, form.vat, form.total].every(
    (s) => s.trim() !== '' && Number.isFinite(Number(s)),
  );
  const preview = numbersOk
    ? matchInvoice(
        { ...data.po, lines: data.lines },
        {
          total: data.prior.total,
          amountOnly: data.prior.amountOnly,
          qty: new Map(data.lines.map((l) => [l.lineNo, l.invoicedBefore])),
        },
        {
          currency: cur,
          subtotal: toMinor(num(form.subtotal)),
          vat: toMinor(num(form.vat)),
          total: toMinor(num(form.total)),
          lines: lines.filter((l) => Number.isFinite(l.qty) && l.qty > 0),
        },
      )
    : null;

  async function record() {
    const errs: Record<string, string> = {};
    if (!form.invoiceNo.trim()) errs.invoiceNo = t('inv.err.required');
    if (!form.invoiceDate) errs.invoiceDate = t('inv.err.required');
    for (const k of ['subtotal', 'vat', 'total'] as const) {
      if (form[k].trim() === '' || !Number.isFinite(Number(form[k])) || Number(form[k]) < 0) {
        errs[k] = t('inv.err.amount');
      }
    }
    if (lines.some((l) => !Number.isFinite(l.qty) || l.qty < 0)) errs.lines = t('inv.err.qty');
    if (!file) errs.file = t('inv.err.file');
    else if (file.size > MAX_FILE) errs.file = t('dl.err.fileSize');
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setNote({ ok: false, text: t('dl.err.fix') });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const out = await apiSend<{ invoiceId: string; match: MatchResult }>(
        'POST',
        `/api/prs/${encodeURIComponent(prNo)}/invoices`,
        {
          etag: data.etag,
          invoiceNo: form.invoiceNo.trim(),
          invoiceDate: form.invoiceDate,
          subtotal: num(form.subtotal),
          vat: num(form.vat),
          total: num(form.total),
          lines: lines.filter((l) => l.qty > 0),
        },
      );
      let text = out.match.matched ? t('inv.doneMatched') : t('inv.doneMismatch');
      let ok = true;
      if (file) {
        try {
          await apiUpload(`/api/prs/${encodeURIComponent(prNo)}/invoices/file`, file, {
            'x-invoice-id': out.invoiceId,
          });
        } catch {
          text = `${text} ${t('inv.fileFailed')}`;
          ok = false;
        }
      }
      setNote({ ok, text });
      setForm(EMPTY);
      setFile(null);
      setErrors({});
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      const fields = e?.fields ?? {};
      setErrors(
        Object.fromEntries(
          Object.keys(fields).map((k) => [
            k,
            k === 'invoiceNo' && fields[k]?.startsWith('Already recorded')
              ? t('inv.err.duplicate')
              : t('dl.err.line'),
          ]),
        ),
      );
      setNote({
        ok: false,
        text:
          e?.code === 'conflict'
            ? t('inv.err.conflict')
            : e?.code === 'invalid_input'
              ? t('dl.err.fix')
              : t('dl.err.save'),
        ref: e?.correlationId ?? null,
      });
    } finally {
      setBusy(false);
    }
  }

  const field = (
    id: keyof Form,
    label: MessageKey,
    opts: { type?: string; dir?: 'ltr' | 'auto'; max?: number; mode?: 'decimal' } = {},
  ) => (
    <div class="field">
      <label for={`inv-${id}`}>{t(label)}</label>
      <input
        id={`inv-${id}`}
        type={opts.type ?? 'text'}
        dir={opts.dir ?? 'ltr'}
        inputMode={opts.mode}
        maxLength={opts.max ?? 40}
        value={form[id]}
        aria-invalid={errors[id] ? true : undefined}
        aria-describedby={
          [errors[id] ? `inv-${id}-err` : '', opts.type === 'date' ? `inv-${id}-when` : '']
            .filter(Boolean)
            .join(' ') || undefined
        }
        onInput={(e) => {
          const value = (e.target as HTMLInputElement).value;
          // Subtotal or total fills the other two at the PO's VAT rate (D96).
          const filled =
            id === 'subtotal' || id === 'total'
              ? fillInvoiceAmounts(id, value, data.po.vatRate)
              : null;
          set(filled ?? { [id]: value });
        }}
      />
      {opts.type === 'date' && <DateHint id={`inv-${id}-when`} value={form[id]} />}
      {errors[id] && (
        <p class="field__error" id={`inv-${id}-err`}>
          {errors[id]}
        </p>
      )}
    </div>
  );

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/queue">{t('nav.queue')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>{' '}
        /{' '}
        <a href={`#/po/${data.poNo}`} dir="ltr">
          {data.poNo}
        </a>
      </p>
      <h2 class="detail-title" dir="auto">
        {data.title}
      </h2>
      <p class="caption" dir="auto">
        {data.supplierName}
      </p>
      {note && (
        <div class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          <p>{note.text}</p>
          {note.ref && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: note.ref })}
            </p>
          )}
        </div>
      )}

      <ul class="tiles" aria-label={t('inv.summary')}>
        {(
          [
            ['inv.poTotal', data.po.total],
            ['inv.invoicedBefore', data.prior.total],
            ['inv.left', Math.max(0, data.po.total - data.prior.total)],
          ] as const
        ).map(([label, value]) => (
          <li key={label} class="tile">
            <span class="caption">{t(label)}</span>
            <span class="tile__value" dir="ltr">
              {cur} {money(value)}
            </span>
          </li>
        ))}
      </ul>

      {data.canRecord && (
        <>
          <section class="card stack" aria-labelledby="inv-new">
            <h3 id="inv-new" class="card__title">
              {t('inv.new')}
            </h3>
            <div class="inv-grid">
              {field('invoiceNo', 'inv.no')}
              {field('invoiceDate', 'inv.date', { type: 'date' })}
              {field('subtotal', 'inv.subtotal', { mode: 'decimal', max: 16 })}
              {field('vat', 'inv.vat', { mode: 'decimal', max: 16 })}
              {field('total', 'inv.total', { mode: 'decimal', max: 16 })}
            </div>
            <p class="caption">
              {t('inv.amountsHint', { currency: cur, rate: String(data.po.vatRate) })}{' '}
              {t('inv.autoFill')}
            </p>
            <div class="table-wrap">
              <table class="table inv-table dl-table">
                <thead>
                  <tr>
                    <th scope="col">{t('dl.col.item')}</th>
                    <th scope="col" class="num dl-wide-only">
                      {t('dl.col.ordered')}
                    </th>
                    <th scope="col" class="num dl-wide-only">
                      {t('inv.col.received')}
                    </th>
                    <th scope="col" class="num dl-wide-only">
                      {t('inv.col.before')}
                    </th>
                    <th scope="col">{t('inv.col.now')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l) => (
                    <tr key={l.lineNo}>
                      <td>
                        <span dir="auto">{l.description}</span>
                        <span class="caption cell-sub">
                          {l.type === 'monthly'
                            ? t('inv.monthly', { n: String(l.months) })
                            : t(`pr.unit.${l.unit}` as MessageKey)}{' '}
                          ·{' '}
                          <span dir="ltr">
                            {cur} {money(l.unitPrice)}
                          </span>
                        </span>
                        <span class="caption cell-sub dl-phone-only">
                          {t('inv.phoneSub', {
                            ordered: l.type === 'monthly' ? String(l.months) : qtyFmt(l.qty),
                            received: l.type === 'monthly' ? '—' : qtyFmt(l.received),
                            before: qtyFmt(l.invoicedBefore),
                          })}
                        </span>
                      </td>
                      <td class="num dl-wide-only" dir="ltr">
                        {l.type === 'monthly' ? `${l.months}` : qtyFmt(l.qty)}
                      </td>
                      <td class="num dl-wide-only" dir="ltr">
                        {l.type === 'monthly' ? '—' : qtyFmt(l.received)}
                      </td>
                      <td class="num dl-wide-only" dir="ltr">
                        {qtyFmt(l.invoicedBefore)}
                      </td>
                      <td>
                        <input
                          class="po-input po-input--num"
                          type="number"
                          min={0}
                          step="any"
                          dir="ltr"
                          aria-label={t('inv.aria.qty', { item: l.description })}
                          value={qty[l.lineNo] ?? '0'}
                          onInput={(e) =>
                            setQty({ ...qty, [l.lineNo]: (e.target as HTMLInputElement).value })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p class="caption">{t('inv.qtyHint')}</p>
            {errors.lines && <p class="field__error">{errors.lines}</p>}
            <div class="field">
              <label for="inv-file">{t('inv.file')}</label>
              <input
                id="inv-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                aria-describedby="inv-file-hint"
                aria-invalid={errors.file ? true : undefined}
                onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
              />
              <p class="caption" id="inv-file-hint">
                {t('dl.fileHint')}
              </p>
              {errors.file && <p class="field__error">{errors.file}</p>}
            </div>
          </section>

          <section class="card stack" aria-labelledby="inv-match" aria-live="polite">
            <div class="card__head">
              <h3 id="inv-match" class="card__title">
                {t('inv.matchTitle')}
              </h3>
              {preview && <MatchPill m={preview} />}
            </div>
            {preview ? (
              <ul class="inv-checks">
                {preview.checks.map((c) => (
                  <CheckLine key={c.id} c={c} currency={cur} />
                ))}
              </ul>
            ) : (
              <p class="caption">{t('inv.matchHint')}</p>
            )}
          </section>

          <div class="actions dl-actions">
            <button
              type="button"
              class="button button--primary"
              disabled={busy}
              onClick={() => void record()}
            >
              {busy
                ? t('dl.saving')
                : preview && !preview.matched
                  ? t('inv.recordMismatch')
                  : t('inv.record')}
            </button>
          </div>
        </>
      )}

      {data.invoices.length > 0 && (
        <section class="card" aria-labelledby="inv-list">
          <h3 id="inv-list" class="card__title">
            {t('inv.recorded')}
          </h3>
          <ul class="action-list">
            {data.invoices.map((i) => (
              <li key={i.invoiceId} class="dl-note">
                <span class="req-row__title" dir="ltr">
                  {i.invoiceNo}
                </span>
                <span class="caption">
                  {formatDate(i.invoiceDate, lang)} ·{' '}
                  <span dir="ltr">
                    {i.currency} {money(i.total)}
                  </span>{' '}
                  · {i.fileName ? <span dir="auto">{i.fileName}</span> : t('inv.noFile')}
                </span>
                <span>
                  <MatchPill m={i.match} />
                </span>
                {!i.match.matched && (
                  <ul class="inv-checks">
                    {i.match.checks
                      .filter((c) => !c.ok)
                      .map((c) => (
                        <CheckLine key={c.id} c={c} currency={i.currency} />
                      ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
